import Decimal from 'decimal.js';
import { AutoBetStopReason, PATTERN_MAP, type Pattern } from '../constants';
import { AutoBetOnWinLossAction } from '../state';

export interface CapResult {
  cappedAmount: number;
  wasCapped: boolean;
}

export interface AutoBetStopConditions {
  currentBetCount: number;
  totalProfit: number;
  numberOfBets: number;
  hasInfiniteBets: boolean;
  stopOnProfitValue: number;
  stopOnLossValue: number;
  currentBalance: number;
  betAmount: number;
}

export interface WinLossCalculationParams {
  currentBetAmount: number;
  initialBetAmount: number;
  action: AutoBetOnWinLossAction;
  increaseValue: number;
  minBet: number;
  maxBet: number;
  decimalScale: number;
}

/**
 * Apply min/max caps to a bet amount
 */
export function applyBetCaps(
  amount: number,
  minBet: number,
  maxBet: number,
  decimalScale: number
): CapResult {
  // Store original amount for comparison
  const originalAmount = amount;

  // Special handling for NaN - treat as sanitization, not capping
  if (Number.isNaN(amount)) {
    return {
      cappedAmount: minBet,
      wasCapped: false,
    };
  }

  // Handle Infinity and negative values
  if (!Number.isFinite(amount) || amount < 0) {
    amount = minBet;
  }

  const cappedAmount = Decimal.max(
    Decimal.min(new Decimal(amount), maxBet),
    minBet
  ).toNumber();

  // Round to 2 decimal places for precision
  const finalAmount = new Decimal(cappedAmount)
    .toDecimalPlaces(decimalScale, Decimal.ROUND_DOWN)
    .toNumber();

  // Check if the amount was actually capped to min or max bounds
  const wasCapped = originalAmount < minBet || originalAmount > maxBet;

  return {
    cappedAmount: finalAmount,
    wasCapped,
  };
}

/**
 * Calculate the next bet amount based on win/loss action
 */
export function calculateNextBetAmount(
  params: WinLossCalculationParams
): CapResult {
  const {
    currentBetAmount,
    initialBetAmount,
    action,
    increaseValue,
    minBet,
    maxBet,
    decimalScale,
  } = params;

  let nextAmount = currentBetAmount;

  if (action === AutoBetOnWinLossAction.RESET) {
    nextAmount = initialBetAmount;
  } else if (action === AutoBetOnWinLossAction.INCREASE_OR_DECREASE) {
    // Calculate percentage increase
    nextAmount = Number(
      new Decimal(currentBetAmount)
        .mul(new Decimal(1).plus(new Decimal(increaseValue).div(100)))
        .toFixed(2)
    );
  }

  return applyBetCaps(nextAmount, minBet, maxBet, decimalScale);
}

/**
 * Determine if a cap notification should be shown based on frequency
 */
export function shouldShowCapNotification(
  counter: number,
  notificationFrequency = 50
): boolean {
  return counter === 1 || counter % notificationFrequency === 0;
}

/**
 * Check if autobetting should stop based on conditions
 */
export function shouldStopAutoBetting(conditions: AutoBetStopConditions): {
  shouldStop: boolean;
  reason?: AutoBetStopReason;
  reasonData?: Record<string, unknown>;
} {
  const {
    currentBetCount,
    totalProfit,
    numberOfBets,
    hasInfiniteBets,
    stopOnProfitValue,
    stopOnLossValue,
    currentBalance,
    betAmount,
  } = conditions;

  // Stop if insufficient balance
  if (currentBalance < betAmount) {
    return {
      shouldStop: true,
      reason: AutoBetStopReason.INSUFFICIENT_BALANCE,
    };
  }

  // Stop if reached maximum number of bets (unless infinite)
  if (!hasInfiniteBets && currentBetCount >= numberOfBets) {
    return {
      shouldStop: true,
      reason: AutoBetStopReason.MAX_BETS_REACHED,
      reasonData: { numberOfBets },
    };
  }

  // Stop if reached profit target
  if (stopOnProfitValue > 0 && totalProfit >= stopOnProfitValue) {
    return {
      shouldStop: true,
      reason: AutoBetStopReason.PROFIT_TARGET_REACHED,
      reasonData: {
        stopOnProfitValue: new Decimal(stopOnProfitValue).toFixed(2),
      },
    };
  }

  // Stop if reached loss limit (negative profit)
  if (stopOnLossValue > 0 && -totalProfit >= stopOnLossValue) {
    return {
      shouldStop: true,
      reason: AutoBetStopReason.STOP_LOSS_REACHED,
      reasonData: {
        stopOnLossValue: new Decimal(stopOnLossValue).toFixed(2),
      },
    };
  }

  return {
    shouldStop: false,
  };
}

/**
 * Validate picked bricks for a given pattern
 */
export function validatePickedBricks(
  pickedBricks: number[],
  pattern: string | number
): {
  isValid: boolean;
  error?: string;
} {
  if (pickedBricks.length === 0) {
    return {
      isValid: false,
      error: 'No bricks picked',
    };
  }

  const patternConfig = PATTERN_MAP[pattern as Pattern];
  if (!patternConfig) {
    return {
      isValid: false,
      error: 'Invalid game pattern',
    };
  }

  const isValid = pickedBricks.every(
    (brick, index) =>
      typeof brick === 'number' &&
      brick >= 0 &&
      brick < patternConfig.cols &&
      index < patternConfig.rows
  );

  if (!isValid) {
    return {
      isValid: false,
      error: 'Invalid brick selection',
    };
  }

  return {
    isValid: true,
  };
}

/**
 * Calculate display amounts from actual amounts using coefficient
 */
export function calculateDisplayAmounts(
  actualWinAmount: number | null | undefined,
  betAmount: number,
  coefficient: number
): {
  displayWinAmount: number;
  currentProfit: number;
  isWin: boolean;
  isLoss: boolean;
} {
  // Handle null/undefined win value - this means a loss
  const winAmount = actualWinAmount ?? 0;

  // For display purposes, we need to calculate profit in display currency
  // If the API returns win in actual currency, we need to convert it back
  const displayWinAmount =
    winAmount > 0
      ? Number(new Decimal(winAmount).div(coefficient).toFixed(2))
      : 0;

  // Calculate profit in display currency (what the user sees)
  // Profit = displayWinAmount - displayBetAmount
  const currentProfit = Number(
    new Decimal(displayWinAmount).minus(betAmount).toFixed(2)
  );

  return {
    displayWinAmount,
    currentProfit,
    isWin: displayWinAmount > betAmount,
    isLoss: displayWinAmount < betAmount,
  };
}

/**
 * Format profit message for display
 */
export function formatProfitMessage(profit: number, currency: string): string {
  const profitValue =
    profit >= 0
      ? `+${new Decimal(profit).toFixed(2)}`
      : new Decimal(profit).toFixed(2);

  return `${profitValue} ${currency}`;
}
